import React, { useEffect } from "react";
import {
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  Stack,
  Box,
  FormHelperText,
} from "@mui/material";

export default function IssueLinkCard({
  t,
  selectedTenant,
  ttlMinutes,
  setTtlMinutes,
  maxUses,
  setMaxUses,
  issuing,
  onIssue,
  issueResult,
  onCopy,
  defaultSuffix,
  labelSuffixOverride,
  setLabelSuffixOverride,

  emailTo,
  setEmailTo,
  recipientName,
  setRecipientName,
  sendingEmail,
  onSendEmail,
}) {
  const token = issueResult?.token;
  const registrationUrl = issueResult?.registration_url;

  // ✅ ALWAYS prefill when tenant changes (even if email UI is hidden)
  useEffect(() => {
    if (!selectedTenant) {
      setEmailTo("");
      setRecipientName("");
      return;
    }

    const email = selectedTenant.contact_admin_email || "";
    const name = `${selectedTenant.contact_admin_first_name || ""} ${selectedTenant.contact_admin_last_name || ""}`.trim();

    setEmailTo(email);
    setRecipientName(name);
  }, [selectedTenant, setEmailTo, setRecipientName, token]); // if tenant has no id, replace with [selectedTenant]

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1">
        {t("SuperAdminTenantsRegLinksPage.issue.title", "Vydanie nového registračného odkazu")}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {selectedTenant
          ? t("SuperAdminTenantsRegLinksPage.issue.defaultSuffixInfo", "Predvolená prípona:") +
            ` ${defaultSuffix || "—"}`
          : t(
              "SuperAdminTenantsRegLinksPage.issue.selectTenantHint",
              "Vyberte organizáciu, aby ste mohli vydávať odkazy."
            )}
      </Typography>

      <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 1 }}>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            type="number"
            label={t("SuperAdminTenantsRegLinksPage.fields.ttlMinutes", "Platnosť (min)")}
            value={ttlMinutes}
            onChange={(e) => setTtlMinutes(e.target.value)}
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            type="number"
            label={t("SuperAdminTenantsRegLinksPage.fields.maxUses", "Max. použitia")}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label={t("SuperAdminTenantsRegLinksPage.fields.labelSuffix", "Prípona názvu (voliteľné)")}
            value={labelSuffixOverride}
            onChange={(e) => setLabelSuffixOverride(e.target.value)}
            placeholder={defaultSuffix || t("SuperAdminTenantsRegLinksPage.placeholders.suffix", "napr. Ján Novák")}
          />
          <FormHelperText>
            {t(
              "SuperAdminTenantsRegLinksPage.fields.labelSuffixHelp",
              "Ak necháte prázdne, použije sa meno admin kontaktu."
            )}
          </FormHelperText>
        </Grid>

        <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "flex-end", pt: { xs: 0, md: 0.5 } }}>
          <Button
            variant="contained"
            onClick={onIssue}
            disabled={issuing || !selectedTenant}
            sx={{ height: 56 }}
          >
            {issuing
              ? t("SuperAdminTenantsRegLinksPage.actions.issuing", "Vydávam…")
              : t("SuperAdminTenantsRegLinksPage.actions.issue", "Vydaj odkaz")}
          </Button>
        </Grid>
      </Grid>

      {issueResult && (
        <>
          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            {registrationUrl && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("SuperAdminTenantsRegLinksPage.result.registrationUrl.title", "Registračná URL")}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                  {registrationUrl}
                </Typography>

                <Stack direction="row" spacing={1} mt={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onCopy(registrationUrl, "SuperAdminTenantsRegLinksPage.snackbar.urlCopied")}
                  >
                    {t("SuperAdminTenantsRegLinksPage.actions.copyUrl", "Kopírovať URL")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => window.open(registrationUrl, "_blank", "noopener,noreferrer")}
                  >
                    {t("SuperAdminTenantsRegLinksPage.actions.open", "Otvoriť")}
                  </Button>
                </Stack>
              </Paper>
            )}

            {token && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("SuperAdminTenantsRegLinksPage.result.token.title", "Token")}
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                  {token}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onCopy(token, "SuperAdminTenantsRegLinksPage.snackbar.tokenCopied")}
                  >
                    {t("SuperAdminTenantsRegLinksPage.actions.copyToken", "Kopírovať token")}
                  </Button>
                </Box>
              </Paper>
            )}

            {token && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("SuperAdminTenantsRegLinksPage.email.title", "Odoslať pozvánku e-mailom")}
                </Typography>

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label={t("SuperAdminTenantsRegLinksPage.email.to", "E-mail príjemcu")}
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder={t("SuperAdminTenantsRegLinksPage.email.toPlaceholder", "napr. jan.mrkvička@firma.sk")}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={t("SuperAdminTenantsRegLinksPage.email.name", "Meno príjemcu (voliteľné)")}
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder={
                        labelSuffixOverride ||
                        defaultSuffix ||
                        t("SuperAdminTenantsRegLinksPage.placeholders.suffix", "napr. Ján Mrkvička")
                      }
                      helperText={t(
                        "SuperAdminTenantsRegLinksPage.email.nameHelp",
                        "Ak necháte prázdne, použije sa prípona názvu."
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={3} sx={{ display: "flex", justifyContent: "flex-end", pt: { xs: 0, md: 0.5 } }}>
                    <Button
                      variant="contained"
                      onClick={onSendEmail}
                      disabled={!selectedTenant || sendingEmail}
                      sx={{ height: 56 }}
                    >
                      {sendingEmail
                        ? t("SuperAdminTenantsRegLinksPage.email.sending", "Odosielam…")
                        : t("SuperAdminTenantsRegLinksPage.email.send", "Odoslať e-mail")}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
}